import React from "react";
import { motion } from "framer-motion";
import apple from "../assets/logos/apple.png";
import intel from "../assets/logos/intel.png";
import etr from "../assets/logos/etr.png";
import sifang from "../assets/logos/sifang.jpeg";
import uoft from "../assets/uoft.png";

const experiences = [
	{
		role: "Research Assistant",
		company: "University of Toronto - Centre for Advanced Nanotechnology",
		startDate: "May 2026",
		endDate: "Present",
		logo: uoft,
		tags: ["Machine Learning"],
		responsibilities: [
			"Research Assistant focusing on machine learning and optical sensor development.",
		],
	},
	{
		role: "Machine Learning Engineer (Manufacturing)",
		company: "Apple Inc.",
		startDate: "Feb 2025",
		endDate: "Aug 2025",
		logo: apple,
		tags: [
			"CNN",
			"SVM",
			"Automatic Optical Inspection",
			"PLC",
			"Closed-Loop Control",
			"Sensor Integration",
			"ML Systems",
			"Web Development",
			"DFM/DFA",
			"Finite Element Analysis",
		],
		responsibilities: [
			"Supported automation system integration for the AirPods Pro 3 assembly line — collaborated with vendors on PLC logic, sensor calibration, AOI system, and closed-loop motion control, improving throughput efficiency by 20s/unit.",
			"Designed and productionized machine learning systems for laser signal analysis and defect detection, and YOLO-based AOI for cosmetic inspection — reduced manual inspection by 80% with real-time production inference.",
			"Built and deployed a cloud manufacturing platform (React + FastAPI + Oracle DB) for real-time production monitoring, defect visualization, and system-level data integration.",
			"Led DFM reviews and CNC + surface-finish DOE to improve yield and reduce cosmetic defects by 35%; redesigned CNC fixtures validated with Ansys FEA, cutting cycle time by 50%.",
		],
	},
	{
		role: "Systems Validation Engineer",
		company: "Intel Corp.",
		startDate: "May 2024",
		endDate: "Jan 2025",
		logo: intel,
		tags: [
			"GPU Validation",
			"Test Infrastructure",
			"Computer Vision",
			"Python",
			"React",
			"Automation",
			"Linux",
		],
		responsibilities: [
			"Designed and executed hardware validation for Arc B580 GPU; built automated test infrastructure across 20+ systems, improving repeatability and thermal stability under continuous operation.",
			"Developed a CNN-based vision tool for automated GPU display corruption detection — reduced manual inspection workload by 80% and enabled scalable validation.",
			"Built a full-stack internal validation platform (React + Express.js + REST APIs) for remote test control, real-time system health dashboards, and workflow automation, adopted across 12+ engineering teams.",
			"Built custom test fixtures and 3D-printed support modules to standardize GPU/debug-card handling; developed Python automation pipelines, reducing manual debug effort by 30%.",
		],
	},
	{
		role: "Software Engineer",
		company: "407 ETR Concession Co. Ltd.",
		startDate: "May 2023",
		endDate: "Aug 2023",
		logo: etr,
		tags: ["AWS Glue", "Redshift", "Python", "ETL Pipelines", "SQL"],
		responsibilities: [
			"Designed and optimized scalable ETL data pipelines using Python, PostgreSQL, AWS Glue, and Amazon Redshift.",
			"Supported AWS cloud migration, replacing legacy on-prem workflows to improve system scalability, reliability, and maintainability.",
			"Collaborated with cross-functional stakeholders to translate business requirements into automated reporting pipelines.",
		],
	},
	{
		role: "Hardware Validation Engineer",
		company: "Beijing Sifang Automation",
		startDate: "May 2021",
		endDate: "Aug 2021",
		logo: sifang,
		tags: [
			"Power Electronics",
			"HV Validation",
			"Simulation",
			"EPLAN",
			"Reliability Testing",
		],
		responsibilities: [
			"Conducted hardware validation and compliance testing on power electronics under high-voltage shock and thermal stress extremes.",
			"Performed power-signal simulations under varying load and fault conditions to validate protection system reliability.",
			"Designed machine and plant layouts for a 35kV substation renovation using EPLAN and produced technical documentation.",
		],
	},
];

export default function WorkExperience() {
	return (
		<section id="work-experience" className="py-12">
			<div className="container mx-auto px-4">
				<h2 className="text-4xl font-bold text-center mb-10 text-gray-900 dark:text-white">
					Work Experience
				</h2>

				<div className="space-y-8">
					{experiences.map((experience, index) => (
						<motion.div
							initial={{ opacity: 0, y: 40 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, margin: "-50px" }}
							transition={{ duration: 0.6, delay: index * 0.1 }}
							key={index}
							className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-6 flex flex-col md:flex-row md:space-x-6 hover:shadow-xl transition-shadow border border-gray-100 dark:border-gray-700"
						>
							<div className="flex-shrink-0 mb-4 md:mb-0">
								<img
									src={experience.logo}
									alt={`${experience.company} logo`}
									className="w-20 h-20 rounded-full object-cover"
									loading="lazy"
								/>
							</div>

							<div>
								<h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
									{experience.role}
								</h3>
								<p className="text-lg text-gray-600 dark:text-gray-300">
									{experience.company}
								</p>
								<p className="text-sm text-gray-500 dark:text-gray-400">
									{experience.startDate} – {experience.endDate || "Present"}
								</p>

								{experience.tags?.length ? (
									<div className="mt-2 mb-3 flex flex-wrap gap-1.5">
										{experience.tags.map((tag, i) => (
											<span
												key={i}
												className="inline-block text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-100 border border-gray-200 dark:border-gray-600"
											>
												{tag}
											</span>
										))}
									</div>
								) : null}

								<ul className="list-disc list-inside space-y-3 text-gray-700 dark:text-gray-300 mt-1">
									{experience.responsibilities.map((line, idx) => (
										<li key={idx} className="leading-relaxed">
											{line}
										</li>
									))}
								</ul>
							</div>
						</motion.div>
					))}
				</div>
			</div>
		</section>
	);
}
